import { message } from "antd";
import { useLanguage } from "../context/LanguageContext";
import messageTranslations from "../utils/messageContent";

const useReportService = () => {
  const { language } = useLanguage();
  const t =
    messageTranslations[language]?.report || messageTranslations["de"].report;

  const downloadPDF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const generateAggregatedReport = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/generateAllFeedbackReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(t.errorAggregate);

      const blob = await response.blob();
      downloadPDF(blob, "AggregatedFeedbackReport.pdf");
      message.success(t.successAggregate);
      return true;
    } catch (error) {
      console.error("generateAggregatedReport:", error.message);
      message.error(t.errorAggregate);
      return null;
    }
  };

  const generateFormReport = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/generateFormReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(t.errorForm);

      const blob = await response.blob();
      downloadPDF(blob, "FormFeedbackReport.pdf");
      message.success(t.successForm);
      return true;
    } catch (error) {
      console.error("generateFormReport:", error.message);
      message.error(t.errorForm);
      return null;
    }
  };

  const generateAggregatedKIReport = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/generateAggregatedKIReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(t.errorAggregateKI);

      const blob = await response.blob();
      downloadPDF(blob, "AggregatedKIFeedbackReport.pdf");
      message.success(t.successAggregateKI);
      return true;
    } catch (error) {
      console.error("generateAggregatedKIReport:", error.message);
      message.error(t.errorAggregateKI);
      return null;
    }
  };

  const generateFormKIReport = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/generateFormKIReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(t.errorFormKI);

      const blob = await response.blob();
      downloadPDF(blob, "FormKIFeedbackReport.pdf");
      message.success(t.successFormKI);
      return true;
    } catch (error) {
      console.error("generateFormKIReport:", error.message);
      message.error(t.errorFormKI);
      return null;
    }
  };

  const getReportsForUser = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/getReportsForUser`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      return response.json();
    } catch (error) {
      message.error(t.loadReportsError);
      return [];
    }
  };

  const getKISuggestions = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/kisuggestions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(t.loadKISuggestionsError);

      const data = await response.json();
      return data?.suggestions || [];
    } catch (error) {
      console.error("getKISuggestions:", error.message);
      return [];
    }
  };

  const getSavedKISuggestions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/getSuggestions`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch (error) {
      console.error("getSavedKISuggestions:", error.message);
      return [];
    }
  };

  return {
    generateAggregatedReport,
    generateFormReport,
    generateAggregatedKIReport,
    generateFormKIReport,
    getReportsForUser,
    getKISuggestions,
    getSavedKISuggestions,
  };
};

export default useReportService;
